import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
    MessageSquare, CheckCircle, Clock, User,
    Search, Filter, Send, Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function InstructorInbox({ user }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [response, setResponse] = useState('');
    const [responding, setResponding] = useState(false);
    const [filter, setFilter] = useState('all'); // all, pending, answered

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const res = await axios.get(`${API}/admin/messages`, { withCredentials: true });
            setMessages(res.data);
        } catch (error) {
            console.error("Error fetching messages:", error);
            toast.error("Erro ao carregar mensagens");
        } finally {
            setLoading(false);
        }
    };

    const handleSendResponse = async () => {
        if (!response.trim()) return;

        try {
            setResponding(true);
            const formData = new FormData();
            formData.append('response', response);

            await axios.put(`${API}/admin/messages/${selectedMessage.message_id}`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success("Resposta enviada com sucesso!");
            setResponse('');
            setSelectedMessage(null);
            fetchMessages(); // Refresh list to show updated status
        } catch (error) {
            console.error("Error sending response:", error);
            toast.error("Erro ao enviar resposta");
        } finally {
            setResponding(false);
        }
    };

    const filteredMessages = messages.filter(msg => {
        if (filter === 'pending') return msg.status === 'pending';
        if (filter === 'answered') return msg.status === 'answered';
        return true;
    });

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold font-heading uppercase text-primary">Inbox do Instrutor</h2>
                <div className="flex gap-2">
                    <Button
                        variant={filter === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter('all')}
                        className="rounded-none font-mono text-xs uppercase"
                    >
                        Todos
                    </Button>
                    <Button
                        variant={filter === 'pending' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter('pending')}
                        className="rounded-none font-mono text-xs uppercase"
                    >
                        Pendentes
                    </Button>
                    <Button
                        variant={filter === 'answered' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter('answered')}
                        className="rounded-none font-mono text-xs uppercase"
                    >
                        Respondidos
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Messages List */}
                <div className="md:col-span-1 border-r border-border pr-6 h-[600px] overflow-y-auto">
                    {loading ? (
                        <div className="text-center py-8 text-muted-foreground">Carregando...</div>
                    ) : filteredMessages.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">Nenhuma mensagem encontrada.</div>
                    ) : (
                        <div className="space-y-4">
                            {filteredMessages.map(msg => (
                                <div
                                    key={msg.message_id}
                                    onClick={() => setSelectedMessage(msg)}
                                    className={`p-4 border cursor-pointer transition-colors tech-card-shadow ${selectedMessage?.message_id === msg.message_id
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border hover:border-primary/50'
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-none uppercase ${msg.status === 'pending'
                                                ? 'bg-yellow-500/10 text-yellow-500'
                                                : 'bg-green-500/10 text-green-500'
                                            }`}>
                                            {msg.status === 'pending' ? 'Pendente' : 'Respondido'}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground">
                                            {new Date(msg.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <h4 className="font-bold text-sm mb-1 truncate">{msg.subject || 'Sem assunto'}</h4>
                                    <p className="text-xs text-muted-foreground mb-2 truncate">{msg.message}</p>
                                    <div className="flex items-center gap-2 text-xs text-primary/80">
                                        <User size={12} />
                                        <span>{msg.user_name}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Message Detail / Response */}
                <div className="md:col-span-2 pl-4">
                    {selectedMessage ? (
                        <Card className="p-6 rounded-none border-border tech-card-shadow h-full flex flex-col">
                            <div className="mb-6 border-b border-border pb-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-xl font-bold font-heading mb-2">{selectedMessage.subject || 'Sem assunto'}</h3>
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground font-mono">
                                            <span className="flex items-center gap-1"><User size={14} /> {selectedMessage.user_name}</span>
                                            <span className="flex items-center gap-1"><Mail size={14} /> {selectedMessage.user_email}</span>
                                            <span className="flex items-center gap-1"><Clock size={14} /> {new Date(selectedMessage.created_at).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    {selectedMessage.status === 'answered' && (
                                        <div className="flex items-center gap-2 text-green-500 bg-green-500/10 px-3 py-1 text-sm font-medium">
                                            <CheckCircle size={16} />
                                            <span>Respondido</span>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-muted/30 p-4 border border-border rounded-none text-sm leading-relaxed whitespace-pre-wrap">
                                    {selectedMessage.message}
                                </div>
                            </div>

                            {selectedMessage.status === 'answered' ? (
                                <div className="bg-green-500/5 p-4 border border-green-500/20 rounded-none mt-auto">
                                    <h4 className="font-bold text-green-500 mb-2 uppercase text-xs tracking-wider">Sua Resposta</h4>
                                    <p className="text-sm whitespace-pre-wrap">{selectedMessage.admin_response}</p>
                                    <p className="text-[10px] text-muted-foreground mt-2">
                                        Respondido por {selectedMessage.responded_by} em {new Date(selectedMessage.responded_at).toLocaleString()}
                                    </p>
                                </div>
                            ) : (
                                <div className="mt-auto">
                                    <label className="text-sm font-medium mb-2 block uppercase tracking-wide">Sua Resposta</label>
                                    <Textarea
                                        value={response}
                                        onChange={(e) => setResponse(e.target.value)}
                                        placeholder="Escreva sua resposta para o aluno..."
                                        className="min-h-[150px] mb-4 rounded-none font-mono text-sm"
                                    />
                                    <div className="flex justify-end">
                                        <Button
                                            onClick={handleSendResponse}
                                            disabled={responding || !response.trim()}
                                            className="rounded-none font-medium tracking-wide uppercase bg-primary hover:bg-primary/90"
                                        >
                                            <Send size={16} className="mr-2" />
                                            {responding ? 'Enviando...' : 'Enviar Resposta'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-none p-12">
                            <MessageSquare size={48} className="mb-4 opacity-20" />
                            <p className="text-lg font-medium">Selecione uma mensagem para ler</p>
                            <p className="text-sm opacity-60">Escolha uma mensagem da lista à esquerda</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
